import { useRef, useEffect, useState } from 'react'
import * as d3 from 'd3'
import dayjs from 'dayjs'
import Box from '@mui/material/Box'
import { useTheme } from '@mui/material/styles'
import { PERSONAS, STACK_ORDER } from './cohorts'

const HEIGHT = 330
const MARGIN = { top: 14, right: 4, bottom: 22, left: 4 }
const BIN_SECONDS = 15 * 60
const BIN_COUNT = 96
const DAY_SECONDS = BIN_COUNT * BIN_SECONDS

// Autofit: block slots shrink so the day's tallest column fits the
// plot, capped above so three users don't become three enormous slabs.
// When even MIN_CELL_HEIGHT cells can't fit one block per user, the
// scale steps up the GRID_LADDER: a block is worth k × k users
// ("1 block = 4 users"). Complete single-persona blocks render as one
// solid square — so blocks are literally countable against the scale
// note — while persona-boundary blocks and the partial remainder break
// into per-user cells (remainders against a ghosted grid), so nothing
// is ever a partial shape.
const MIN_CELL_HEIGHT = 2
const MAX_BLOCK_SLOT = 16
const GRID_LADDER = [1, 2, 4]
// The live column renders enlarged — a lens over "now" — anchored at
// the baseline. It gets a genuinely wider slot (LIVE_SLOT × normal, the
// other 95 columns compress a hair to pay for it) so the scaled-up
// blocks have breathing room instead of overlapping their neighbors.
const LIVE_SCALE = 1.4
const LIVE_SLOT = 1.9

const minBlockSlot = (k) => k * MIN_CELL_HEIGHT + (k === 1 ? 0 : 1)

// Smallest grid whose blocks still fit the plot at legible cell size.
// The ladder's end is a hard ceiling: past it, columns climb off-plot
// (honestly "off the chart") rather than clipping to a fake cap.
const chooseGrid = (dayPeak, plotH) =>
  GRID_LADDER.find(
    (k) => plotH / Math.ceil(dayPeak / (k * k)) >= minBlockSlot(k)
  ) || GRID_LADDER[GRID_LADDER.length - 1]

const hourLabel = (hour) => {
  if (hour === 0) return '12a'
  if (hour < 12) return `${hour}a`
  if (hour === 12) return '12p'
  return `${hour - 12}p`
}

// Re-bin the raw cohort series into 96 fixed 15-minute local-day bins.
// The server's step drifts with the window (30s early in the day, ~173s
// by midnight), so bins are derived client-side to stay stable. Each bin
// keeps the single busiest sample's persona breakdown: per-persona
// maxima taken independently could stack above the true concurrent
// peak, while one timestamp is an exact snapshot of a real moment.
const binDay = (series, dayStart) => {
  const samples = new Map()
  series.forEach((entry) => {
    const persona = PERSONAS.find(({ match }) => match(entry.labels || {}))
    if (!persona) return
    entry.points.forEach(([ts, value]) => {
      let sample = samples.get(ts)
      if (!sample) {
        sample = { total: 0, counts: {} }
        samples.set(ts, sample)
      }
      const count = Math.round(value)
      sample.counts[persona.key] = (sample.counts[persona.key] || 0) + count
      sample.total += count
    })
  })

  const bins = Array.from({ length: BIN_COUNT }, (unused, index) => ({
    index,
    start: dayStart + index * BIN_SECONDS,
    total: 0,
    counts: {},
  }))
  samples.forEach((sample, ts) => {
    const index = Math.floor((ts - dayStart) / BIN_SECONDS)
    if (index < 0 || index >= BIN_COUNT) return
    if (sample.total > bins[index].total) {
      bins[index].total = sample.total
      bins[index].counts = sample.counts
    }
  })
  return bins
}

// One entry per user, bottom-up in stack order (POWER at the base,
// GUEST on top). maxCells is only a render guard against drawing rects
// far off the top of the svg — it is not a visual cap (overflow columns
// visibly run off the plot).
const toCells = (bin, maxCells) => {
  const cells = []
  STACK_ORDER.forEach(({ key, color }) => {
    for (let i = bin.counts[key] || 0; i > 0; i -= 1) {
      cells.push({ index: cells.length, color })
    }
  })
  return cells.slice(0, maxCells)
}

// Groups cells into render items: a complete block whose cells all
// share one persona merges into a single solid rect (span = perBlock);
// boundary and partial blocks keep their per-user cells (span = 1).
// Purely a rendering merge — geometry and totals are untouched.
const toItems = (cells, perBlock) => {
  const items = []
  for (let start = 0; start < cells.length; start += perBlock) {
    const block = cells.slice(start, start + perBlock)
    const solid =
      block.length === perBlock &&
      block.every(({ color }) => color === block[0].color)
    if (solid) {
      items.push({
        key: `b${start}`,
        solid: true,
        index: start,
        span: perBlock,
        color: block[0].color,
      })
    } else {
      block.forEach(({ index, color }) =>
        items.push({ key: `c${index}`, solid: false, index, span: 1, color })
      )
    }
  }
  return items
}

const renderBins = (state, series, dayStart, hasAnimatedRef) => {
  const bins = binDay(series, dayStart)
  const now = Math.min(
    Math.max(Date.now() / 1000, dayStart),
    dayStart + DAY_SECONDS
  )
  const liveIndex = Math.min(
    BIN_COUNT - 1,
    Math.floor((now - dayStart) / BIN_SECONDS)
  )
  const dayPeak = Math.max(1, ...bins.map(({ total }) => total))
  // The enlarged live column needs LIVE_SCALE times its height, so it
  // counts as proportionally taller when sizing the grid.
  const liveTotal = bins[liveIndex] ? bins[liveIndex].total : 0
  const fitPeak = Math.max(dayPeak, Math.ceil(liveTotal * LIVE_SCALE))
  const k = chooseGrid(fitPeak, state.plotH)
  const perBlock = k * k
  // Blocks never grow taller than they are wide, so cells stay square
  // whatever width the panel gets.
  const maxSlot = Math.min(MAX_BLOCK_SLOT, state.blockW + 1)
  const blockSlotH = Math.max(
    minBlockSlot(k),
    Math.min(maxSlot, state.plotH / Math.ceil(fitPeak / perBlock))
  )
  const blockGap = k === 1 ? 0 : blockSlotH >= 12 ? 2 : 1
  const cellH = (blockSlotH - blockGap) / k
  const cellW = state.blockW / k
  const cellRectH = Math.max(1, cellH - (cellH >= 3.5 ? 1 : 0.4))
  const cellRectW = k === 1 ? state.blockW : Math.max(1, cellW - 1)
  const cornerR = Math.min(2, cellRectH / 3, cellRectW / 3)
  const maxCells =
    perBlock * Math.ceil((state.plotH + MARGIN.top) / blockSlotH + 1)
  const cascade = !hasAnimatedRef.current

  // Cell index → grid position. The bottom row of a block fills first
  // (bottom-left, bottom-right, then the row above) so a partial block
  // raises the column's silhouette in row-sized steps.
  const cellPos = ({ index }) => {
    const block = Math.floor(index / perBlock)
    const row = Math.floor((index % perBlock) / k)
    const col = (index % perBlock) % k
    return {
      x: state.gapX + col * cellW,
      y:
        state.baseline -
        block * blockSlotH -
        (row + 1) * cellH +
        (cellH - cellRectH),
    }
  }
  const finalX = (d) => cellPos(d).x
  const finalY = (d) => cellPos(d).y

  // Solid full blocks span their whole k × k footprint as one rect;
  // cells keep their grid position within the block.
  const itemRect = (item) => {
    if (!item.solid) {
      return {
        x: finalX(item),
        y: finalY(item),
        w: cellRectW,
        h: cellRectH,
        r: cornerR,
      }
    }
    const block = Math.floor(item.index / perBlock)
    const w = k === 1 ? state.blockW : (k - 1) * cellW + cellRectW
    const h = (k - 1) * cellH + cellRectH
    return {
      x: state.gapX,
      y: state.baseline - block * blockSlotH - k * cellH + (cellH - cellRectH),
      w,
      h,
      // Same subtle radius as the cells — solid blocks read as bigger
      // bricks in the same visual language, not as pills.
      r: cornerR,
    }
  }

  state.bins = bins
  state.liveIndex = liveIndex
  state.scaleNote.text(k > 1 ? `1 block = ${perBlock} users` : '')

  // Left edge of each column's slot: uniform, except everything after
  // the live column is pushed right by the lens slot's extra width.
  const colX = (index) =>
    MARGIN.left +
    index * state.slot +
    (index > liveIndex ? state.wideSlot - state.slot : 0)

  state.columns.each(function renderColumn(colIndex) {
    const bin = bins[colIndex]
    const isLive = colIndex === liveIndex
    const signature = [
      PERSONAS.map(({ key }) => bin.counts[key] || 0).join(','),
      blockSlotH.toFixed(2),
      k,
      colIndex > liveIndex,
      isLive,
    ].join('|')
    if (!cascade && state.signatures[colIndex] === signature) return
    // A freshly rebuilt skeleton (resize) renders statically; the
    // fall-in is reserved for the initial cascade and for genuine data
    // changes, which on a typical poll is only the live column.
    const animate = cascade || state.signatures[colIndex] !== undefined
    state.signatures[colIndex] = signature

    const colG = d3.select(this)
    const cells = toCells(bin, maxCells)

    // The lens: the live column scales up around its slot center while
    // staying seated on the baseline, centered in its wider slot; when
    // the live edge moves on, the column eases back to normal size.
    const transform = isLive
      ? `translate(${
          colX(colIndex) + (state.wideSlot - state.slot) / 2
        },0) translate(${state.slot / 2},${
          state.baseline
        }) scale(${LIVE_SCALE}) translate(${
          -state.slot / 2
        },${-state.baseline})`
      : `translate(${colX(colIndex)},0)`
    if (animate) {
      colG
        .transition('zoom')
        .duration(400)
        .ease(d3.easeCubicOut)
        .attr('transform', transform)
    } else {
      colG.attr('transform', transform)
    }

    // Past-but-empty bins get a baseline tick; the future wash covers
    // bins not yet lived, so an empty evening reads differently from an
    // evening still to come.
    colG
      .select('rect.tick')
      .style('opacity', colIndex <= liveIndex && bin.total === 0 ? 0.3 : 0)

    // Ghost the unfilled cells of a partial top block so it reads as
    // partially filled rather than as a whole extra block (k > 1 only;
    // skipped when the column overflowed past the render guard).
    const ghosts = []
    if (k > 1 && cells.length === bin.total && bin.total % perBlock !== 0) {
      const blockEnd = Math.ceil(bin.total / perBlock) * perBlock
      for (let index = bin.total; index < blockEnd; index += 1) {
        ghosts.push({ index })
      }
    }
    const ghostRects = colG
      .selectAll('rect.ghost')
      .data(ghosts, ({ index }) => index)
    ghostRects.exit().remove()
    ghostRects
      .enter()
      .append('rect')
      .attr('class', 'ghost')
      .attr('fill', state.gridColor)
      .attr('fill-opacity', 0.08)
      .merge(ghostRects)
      .attr('x', finalX)
      .attr('width', cellRectW)
      .attr('y', finalY)
      .attr('height', cellRectH)
      .attr('rx', cornerR)

    const items = toItems(cells, perBlock)
    const selection = colG.selectAll('rect.block')
    // Cells covered by the previous render (blocks count as perBlock),
    // so newly-arrived users' delays start where the old stack ended.
    const prevCells = d3.sum(selection.data(), (d) => d.span || 1)
    const rects = selection.data(items, ({ key }) => key)
    const stackDelay = Math.min(45, 700 / Math.max(1, cells.length))

    rects
      .exit()
      .transition('fall')
      .duration(300)
      .ease(d3.easeCubicIn)
      .attr('y', state.baseline + 8)
      .style('opacity', 0)
      .remove()

    const entered = rects
      .enter()
      .append('rect')
      .attr('class', 'block')
      .attr('x', (d) => itemRect(d).x)
      .attr('width', (d) => itemRect(d).w)
      .attr('rx', (d) => itemRect(d).r)
      .attr('height', (d) => itemRect(d).h)
      .attr('fill', ({ color }) => color)

    if (animate) {
      // Fall from above the plot's clip edge; the bottom item lands
      // first, and the live column gets the longer, springier drop.
      entered
        .attr('y', ({ index }) => -((cells.length - index) / k) * cellH - 8)
        .transition('fall')
        .duration(isLive ? 800 : 600)
        .delay(
          ({ index }) =>
            (cascade ? colIndex * 6 : 0) +
            Math.max(0, index - prevCells) * stackDelay
        )
        .ease(d3.easeBounceOut)
        .attr('y', (d) => itemRect(d).y)
    } else {
      entered.attr('y', (d) => itemRect(d).y)
    }

    // Surviving items tween to their new size/position (this is the
    // whole-field re-scale when the day's peak grows or the grid steps
    // up the ladder) — no bounce.
    rects
      .transition('reflow')
      .duration(400)
      .ease(d3.easeCubicOut)
      .attr('x', (d) => itemRect(d).x)
      .attr('width', (d) => itemRect(d).w)
      .attr('height', (d) => itemRect(d).h)
      .attr('rx', (d) => itemRect(d).r)
      .attr('fill', ({ color }) => color)
      .attr('y', (d) => itemRect(d).y)
  })
  hasAnimatedRef.current = true

  // The axis chrome follows the lens layout: hour marks are placed at
  // their bin's shifted left edge, and hover targets match each slot.
  state.hourLines.forEach(({ hour, line }) => {
    const x = colX(hour * 4)
    line.transition().duration(400).attr('x1', x).attr('x2', x)
  })
  state.hourLabels.forEach(({ hour, text }) => {
    text
      .transition()
      .duration(400)
      .attr('x', colX(hour * 4))
  })
  state.hitRects
    .attr('x', (index) => colX(index))
    .attr('width', (index) =>
      index === liveIndex ? state.wideSlot : state.slot
    )

  const binFraction = Math.min(
    1,
    Math.max(0, (now - (dayStart + liveIndex * BIN_SECONDS)) / BIN_SECONDS)
  )
  const nowX = colX(liveIndex) + binFraction * state.wideSlot
  state.nowLine
    .transition()
    .duration(400)
    .attr('x1', nowX)
    .attr('x2', nowX)
    .style('opacity', 0.3)
  const washX = colX(liveIndex) + state.wideSlot
  state.wash
    .attr('x', washX)
    .attr('width', Math.max(0, MARGIN.left + state.plotW - washX))
}

// A fixed 24-hour field of 96 fifteen-minute columns, each a bottom-up
// stack of persona-colored blocks. The axis is pinned midnight to
// midnight so the past never moves and column widths stay comparable
// all day; new data falls into place at the live edge.
const DayBlocks = ({ series, dayStart, subscribeSparkles }) => {
  const containerRef = useRef(null)
  const tooltipRef = useRef(null)
  const stateRef = useRef(null)
  // Survives skeleton rebuilds (resize) so the initial left-to-right
  // cascade only ever plays once per mount.
  const hasAnimatedRef = useRef(false)
  const [width, setWidth] = useState(0)
  const theme = useTheme()

  useEffect(() => {
    const element = containerRef.current
    setWidth(element.offsetWidth)
    const observer = new ResizeObserver((entries) => {
      const next = Math.round(entries[0].contentRect.width)
      setWidth((prev) => (Math.abs(prev - next) > 1 ? next : prev))
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  // Static chrome — gridlines, hour labels, per-bin groups, glow, now
  // marker, hover targets — rebuilt only on width/theme changes. Data
  // renders mutate the state object this effect creates.
  useEffect(() => {
    if (!width) return undefined
    const container = containerRef.current

    const plotW = width - MARGIN.left - MARGIN.right
    const plotH = HEIGHT - MARGIN.top - MARGIN.bottom
    const baseline = MARGIN.top + plotH
    // 95 normal slots plus one lens-width slot fill the plot exactly.
    const slot = plotW / (BIN_COUNT - 1 + LIVE_SLOT)
    const wideSlot = slot * LIVE_SLOT
    const blockW = Math.max(1, slot - 2)
    const gapX = (slot - blockW) / 2
    const gridColor = theme.palette.text.secondary

    const svg = d3
      .select(container)
      .append('svg')
      .attr('width', width)
      .attr('height', HEIGHT)

    // Hour marks are positioned by renderBins (their bin's left edge
    // shifts with the lens layout), so keep references to them.
    const grid = svg.append('g')
    const hourLines = []
    for (let hour = 1; hour < 24; hour += 1) {
      const x = MARGIN.left + hour * 4 * slot
      hourLines.push({
        hour,
        line: grid
          .append('line')
          .attr('x1', x)
          .attr('x2', x)
          .attr('y1', MARGIN.top)
          .attr('y2', baseline)
          .attr('stroke', gridColor)
          .attr('stroke-opacity', hour % 3 === 0 ? 0.15 : 0.05),
      })
    }
    const hourLabels = []
    for (let hour = 0; hour < 24; hour += 3) {
      hourLabels.push({
        hour,
        text: svg
          .append('text')
          .attr('x', MARGIN.left + hour * 4 * slot)
          .attr('y', baseline + 15)
          .attr('text-anchor', hour === 0 ? 'start' : 'middle')
          .style('font-size', '10px')
          .style('fill', gridColor)
          .text(hourLabel(hour)),
      })
    }

    // Dims (never brightens) the not-yet-lived part of the day, on
    // light and dark themes alike.
    const wash = svg
      .append('rect')
      .attr('y', MARGIN.top)
      .attr('height', plotH)
      .attr('fill', '#000')
      .attr('fill-opacity', 0.15)
      .attr('width', 0)

    const columns = svg
      .append('g')
      .selectAll('g')
      .data(d3.range(BIN_COUNT))
      .join('g')
      .attr(
        'transform',
        (index) => `translate(${MARGIN.left + index * slot},0)`
      )

    columns
      .append('rect')
      .attr('class', 'tick')
      .attr('x', gapX)
      .attr('width', blockW)
      .attr('y', baseline - 1.5)
      .attr('height', 1.5)
      .attr('fill', gridColor)
      .style('opacity', 0)

    // Announces the quantized scale ("1 block = 4 users") whenever the
    // grid ladder is past one-user-per-block; empty otherwise.
    const scaleNote = svg
      .append('text')
      .attr('x', width - MARGIN.right)
      .attr('y', 9)
      .attr('text-anchor', 'end')
      .style('font-size', '10px')
      .style('fill', gridColor)

    const nowLine = svg
      .append('line')
      .attr('y1', MARGIN.top - 8)
      .attr('y2', baseline)
      .attr('stroke', theme.palette.primary.main)
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3 3')
      .style('opacity', 0)

    const state = {
      columns,
      wash,
      nowLine,
      scaleNote,
      hourLines,
      hourLabels,
      gridColor,
      plotW,
      plotH,
      baseline,
      slot,
      wideSlot,
      blockW,
      gapX,
      signatures: [],
      bins: [],
      liveIndex: -1,
    }

    const tooltip = tooltipRef.current
    const showTip = (event, index) => {
      const bin = state.bins[index]
      if (!bin || index > state.liveIndex) return
      const from = dayjs.unix(bin.start).format('h:mm')
      const to = dayjs.unix(bin.start + BIN_SECONDS).format('h:mm A')
      const live = index === state.liveIndex ? ' · live' : ''
      // Biggest group first (stable sort, ties keep the PERSONAS order).
      const rows = PERSONAS.filter(({ key }) => bin.counts[key])
        .sort((a, b) => bin.counts[b.key] - bin.counts[a.key])
        .map(
          ({ key, color }) =>
            `<span style="color:${color}">■</span> ${key} <b>${bin.counts[key]}</b>`
        )
      const body = rows.length
        ? `${rows.join('<br/>')}<br/>Total <b>${bin.total}</b>`
        : 'No one on'
      tooltip.innerHTML = `<b>${from} – ${to}${live}</b><br/>${body}`
      tooltip.style.opacity = 1
    }
    const moveTip = (event) => {
      // Pointer coords come back in visual (post-transform) pixels;
      // divide by the wallboard scale (visual width over layout width)
      // to get back to local coordinates.
      const scale =
        container.getBoundingClientRect().width / container.offsetWidth || 1
      const [rawX, rawY] = d3.pointer(event, container)
      const x = rawX / scale
      const y = rawY / scale
      // Flip to the left of the cursor near the right edge so the
      // tooltip stays inside the panel.
      const flip = x > width * 0.8
      tooltip.style.left = flip ? 'auto' : `${x + 12}px`
      tooltip.style.right = flip ? `${width - x + 12}px` : 'auto'
      tooltip.style.top = `${y - 12}px`
    }
    const hideTip = () => {
      tooltip.style.opacity = 0
    }

    // Hover targets; x/width follow the lens layout via renderBins.
    state.hitRects = svg
      .append('g')
      .selectAll('rect')
      .data(d3.range(BIN_COUNT))
      .join('rect')
      .attr('x', (index) => MARGIN.left + index * slot)
      .attr('y', MARGIN.top)
      .attr('width', slot)
      .attr('height', plotH)
      .attr('fill', 'transparent')
      .on('mouseenter', showTip)
      .on('mousemove', moveTip)
      .on('mouseleave', hideTip)

    stateRef.current = state
    return () => {
      container.innerHTML = ''
      stateRef.current = null
    }
  }, [width, theme])

  useEffect(() => {
    const state = stateRef.current
    if (!state || !series) return
    renderBins(state, series, dayStart, hasAnimatedRef)
  }, [series, dayStart, width, theme])

  // A clip event blinks one rect in the live column's band for the
  // clipper's persona. Cells are anonymous counts, so a random pick
  // within the correct band is the honest approximation of "their"
  // cell (in 1-block-=-N-users mode the block still contains them).
  // An empty band (stale lease / data lag) just skips the blink.
  useEffect(() => {
    if (!subscribeSparkles) return undefined
    return subscribeSparkles((event) => {
      // Only capture events blink the blocks; the later quote event is
      // the bubble panel's alone.
      if (event.type !== 'create') return
      const state = stateRef.current
      const persona = PERSONAS.find(({ key }) => key === event.personaKey)
      if (!state || !persona || state.liveIndex < 0) return
      const liveColumn = state.columns.filter(
        (index) => index === state.liveIndex
      )
      const rects = liveColumn
        .selectAll('rect.block')
        .filter(({ color }) => color === persona.color)
        .nodes()
      if (rects.length === 0) return
      d3.select(rects[Math.floor(Math.random() * rects.length)])
        .transition('sparkle')
        .duration(120)
        .attr('fill', '#ffffff')
        .transition()
        .duration(1400)
        .ease(d3.easeCubicOut)
        .attr('fill', persona.color)
    })
  }, [subscribeSparkles])

  return (
    <Box sx={{ position: 'relative' }}>
      <Box ref={containerRef} sx={{ width: '100%', height: HEIGHT }} />
      <Box
        ref={tooltipRef}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          opacity: 0,
          pointerEvents: 'none',
          transition: 'opacity 0.15s',
          bgcolor: 'rgba(0, 0, 0, 0.8)',
          color: 'white',
          px: 1.25,
          py: 0.5,
          borderRadius: 1,
          fontSize: 12,
          lineHeight: 1.6,
          whiteSpace: 'nowrap',
          zIndex: 1,
        }}
      />
    </Box>
  )
}

export default DayBlocks
